import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Calendar, Car, Clock, FileText, CreditCard, Receipt, Ticket, Users, Wrench } from 'lucide-react';
import { ACTION_VARIANT, ValueChange } from '../components/RecordHistory';
import { Badge } from '../components/ui/Badge';
import { Card, CardHeader, EmptyState, PageHeader } from '../components/ui/Card';
import { PageLoader } from '../components/ui/Spinner';
import { useDashboardStats, useDocuments, useUpcomingServices } from '../hooks/useApi';
import { useIsAdmin } from '../store';
import {
  ACTION_LABELS,
  cn,
  daysUntil,
  formatDate,
  formatDateTime,
  formatNumber,
  TABLE_LABELS,
} from '../utils/helpers';
import { t } from '../i18n';

function StatCard({ label, value, icon, href, tone }: { label: string; value?: number; icon: ReactNode; href: string; tone: string }) {
  return (
    <Link to={href} className="group">
      <Card padding="sm" className="h-full transition-shadow group-hover:shadow-md">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm text-secondary-500">{label}</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-secondary-900">{value === undefined ? '–' : formatNumber(value)}</p>
          </div>
          <div className={cn('rounded-xl p-2.5', tone)}>{icon}</div>
        </div>
      </Card>
    </Link>
  );
}

export function Dashboard() {
  const isAdmin = useIsAdmin();
  const { data: stats, isLoading } = useDashboardStats();
  const { data: upcoming, isLoading: upcomingLoading } = useUpcomingServices(30);
  const { data: expiring, isLoading: docsLoading } = useDocuments({ expiring_soon: true, expired: true, page_size: 8 });

  if (isLoading) return <PageLoader />;

  const alerts = (stats?.upcoming_services ?? 0) + (stats?.expiring_documents ?? 0) + (stats?.expiring_licenses ?? 0) + (stats?.expiring_vignettes ?? 0);

  return (
    <>
      <PageHeader title={t('Prehľad')} subtitle={t('Stav vozového parku a úlohy vyžadujúce pozornosť')} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label={t('Vozidlá v evidencii')} value={stats?.total_cars} href="/cars" tone="bg-primary-100 text-primary-700" icon={<Car className="h-6 w-6" />} />
        <StatCard label={t('Aktívne vozidlá')} value={stats?.active_cars} href="/cars?status=active" tone="bg-green-100 text-green-700" icon={<Car className="h-6 w-6" />} />
        <StatCard label={t('V servise')} value={stats?.cars_in_service} href="/cars?status=service" tone="bg-amber-100 text-amber-700" icon={<Wrench className="h-6 w-6" />} />
        <StatCard label={t('Aktívni vodiči')} value={stats?.total_drivers} href="/drivers" tone="bg-violet-100 text-violet-700" icon={<Users className="h-6 w-6" />} />
        <StatCard label={t('Požičané vozidlá')} value={stats?.active_assignments} href="/assignments?active_only=true" tone="bg-sky-100 text-sky-700" icon={<Calendar className="h-6 w-6" />} />
        <StatCard label={t('Servisy do 30 dní')} value={stats?.upcoming_services} href="/maintenance" tone="bg-red-100 text-red-700" icon={<Wrench className="h-6 w-6" />} />
        <StatCard label={t('Končiace dokumenty')} value={stats?.expiring_documents} href="/documents?validity=expiring" tone="bg-orange-100 text-orange-700" icon={<FileText className="h-6 w-6" />} />
        <StatCard label={t('Končiace vodičáky')} value={stats?.expiring_licenses} href="/drivers?license=expiring" tone="bg-pink-100 text-pink-700" icon={<CreditCard className="h-6 w-6" />} />
        <StatCard label={t('Známky končiace do 30 dní')} value={stats?.expiring_vignettes} href="/vignettes" tone="bg-teal-100 text-teal-700" icon={<Ticket className="h-6 w-6" />} />
        <StatCard label={t('Nezaplatené pokuty')} value={stats?.unpaid_fines} href="/fines?year=&paid=false" tone="bg-rose-100 text-rose-700" icon={<Receipt className="h-6 w-6" />} />
      </div>

      {alerts > 0 && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {t('Máte {alerts} upozornení na blížiace sa termíny (servisy, platnosť dokumentov, vodičských preukazov alebo diaľničných známok).', { alerts })}
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card padding="none">
          <div className="p-5 pb-0">
            <CardHeader title={t('Plánované servisy')} subtitle={t('Po termíne alebo do 30 dní / 1 500 km')} action={<Link to="/services?tab=upcoming" className="text-sm text-primary-600 hover:underline">{t('Všetko')}</Link>} />
          </div>
          {upcomingLoading ? (
            <PageLoader />
          ) : !upcoming?.length ? (
            <EmptyState icon={<Wrench className="h-10 w-10" />} title={t('Žiadne blížiace sa servisy')} />
          ) : (
            <ul className="divide-y divide-secondary-100">
              {upcoming.slice(0, 8).map(({ service, days_left, km_left, overdue }) => (
                <li key={service.id}>
                  <Link to={`/cars/${service.car_id}?tab=services`} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-secondary-50">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-secondary-900">
                        {service.car?.license_plate} <span className="font-normal text-secondary-500">{service.car?.brand} {service.car?.model}</span>
                      </p>
                      <p className="text-sm text-secondary-500">
                        {service.next_service_date && `do ${formatDate(service.next_service_date)}`}
                        {service.next_service_date && service.next_service_km !== null && ' · '}
                        {service.next_service_km !== null && `pri ${formatNumber(service.next_service_km)} km`}
                      </p>
                    </div>
                    <Badge variant={overdue ? 'danger' : 'warning'}>
                      {overdue
                        ? t('Po termíne')
                        : days_left !== null && (km_left === null || days_left <= 30)
                          ? t('o {daysLeft} dní', { daysLeft: days_left })
                          : t('zostáva {kmLeft} km', { kmLeft: formatNumber(km_left) })}
                    </Badge>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card padding="none">
          <div className="p-5 pb-0">
            <CardHeader title={t('Končiaca platnosť dokumentov')} subtitle={t('Neplatné alebo končiace do 30 dní')} action={<Link to="/documents?validity=expiring" className="text-sm text-primary-600 hover:underline">{t('Všetko')}</Link>} />
          </div>
          {docsLoading ? (
            <PageLoader />
          ) : !expiring?.documents.length ? (
            <EmptyState icon={<FileText className="h-10 w-10" />} title={t('Žiadne končiace dokumenty')} />
          ) : (
            <ul className="divide-y divide-secondary-100">
              {expiring.documents.map((doc) => {
                const days = daysUntil(doc.expiry_date);
                return (
                  <li key={doc.id}>
                    <Link to={`/cars/${doc.car_id}?tab=documents`} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-secondary-50">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-secondary-900">{doc.title}</p>
                        <p className="text-sm text-secondary-500">{doc.car?.license_plate} · {doc.car?.brand} {doc.car?.model}</p>
                      </div>
                      <Badge variant={days !== null && days < 0 ? 'danger' : 'warning'}>
                        {days !== null && days < 0 ? t('neplatné od {expiryDate}', { expiryDate: formatDate(doc.expiry_date) }) : `do ${formatDate(doc.expiry_date)}`}
                      </Badge>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      {isAdmin && (
        <Card padding="none" className="mt-6">
          <div className="p-5 pb-0">
            <CardHeader title={t('Posledné zmeny')} action={<Link to="/audit" className="text-sm text-primary-600 hover:underline">{t('Audit log')}</Link>} />
          </div>
          {!stats?.recent_activity.length ? (
            <EmptyState icon={<Clock className="h-10 w-10" />} title={t('Zatiaľ žiadna aktivita')} />
          ) : (
            <ul className="divide-y divide-secondary-100 text-sm">
              {stats.recent_activity.map((log) => (
                <li key={log.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-2.5">
                  <span className="w-32 shrink-0 text-secondary-500">{formatDateTime(log.timestamp)}</span>
                  <Badge variant={ACTION_VARIANT[log.action] ?? 'default'}>{ACTION_LABELS[log.action] ?? log.action}</Badge>
                  <span className="text-secondary-700">{TABLE_LABELS[log.table_name] ?? log.table_name} #{log.record_id}</span>
                  <span className="min-w-0 flex-1 truncate">
                    <ValueChange log={log} />
                  </span>
                  <span className="text-secondary-500">{log.user?.username ?? t('systém')}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </>
  );
}
